import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { lessonSchema } from "./useLesson";

export const courseProgressSchema = z.object({
  id: z.string(),
  completed: z.boolean(),
  courseId: z.string(),
  userId: z.string(),
  currentLessonId: z.string().nullable().optional(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const courseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  bannerImage: z.string(),
  lessons: z.array(lessonSchema).optional().default([]),
  courseProgress: z.array(courseProgressSchema).optional().default([]),
  archived: z.boolean(),
  featured: z.boolean(),
  publishedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Course = z.infer<typeof courseSchema>;
export type CourseProgress = z.infer<typeof courseProgressSchema>;

export const getAllCourses = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/courses`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return courseSchema.array().parse(response.data);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  });
};

export const getCourseById = (courseId: string) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["course", courseId],
    queryFn: async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/courses/${courseId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return courseSchema.parse(response.data);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    enabled: !!courseId,
  });
};

export const getCourseProgress = (courseId: string) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["courseProgress", courseId],
    queryFn: async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/courses/${courseId}/progress`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return courseProgressSchema.parse(response.data);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    enabled: !!courseId,
  });
};

export const upsertCourseProgress = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      courseId,
      completed,
      currentLessonId,
    }: {
      courseId: string;
      completed: boolean;
      currentLessonId?: string;
    }) => {
      const token = await getToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/courses/${courseId}/progress`,
        {
          completed,
          currentLessonId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return courseProgressSchema.parse(response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["courseProgress", variables.courseId] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["course", variables.courseId] });
    },
  });
};